import noop from 'lodash-es/noop';
import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { autobind } from 'core-decorators';
import styled, { keyframes } from 'styled-components';
import variables from '../../variables';

const MODAL_TRANSITION_DURATION = 200;

const onDialogMaskKeydown = e => {
  e.stopPropagation();
};

const popout = keyframes`{
  0% { transform: scale(0) }
  55% { transform: scale(2) }
  75% { transform: scale(.9) }
  80% { transform: scale(1.1) }
  100% { transform: scale(1) }
}`;

const DialogMask = styled.div`
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: ${props =>
    props.backgroundColor ? props.backgroundColor : 'rgba(0, 0, 0, 0.75)'};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  transition: all ${MODAL_TRANSITION_DURATION}ms ease-in-out;
  opacity: ${props => (props.visible ? '1' : '0')};
  visibility: ${props => (props.visible ? 'visible' : 'hidden')};
  z-index: ${props => (props.visible ? '200' : '-1')};
  color: ${variables.colors.black};
`;
DialogMask.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  onKeyDown: PropTypes.func,
};
DialogMask.defaultProps = {
  onKeyDown: onDialogMaskKeydown,
};

const DialogWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  width: ${props => (props.autoGrow ? 'auto' : '480px')};
  max-width: 100%;
  max-height: 100%;
  box-shadow: 0 50px 50px 0 rgba(0, 0, 0, 0.25);
  transition: all 0.2s ease-in-out;
  border-radius: 10px;
  transform: ${props => (props.visible ? 'scale(1)' : 'scale(0.95)')};
`;
DialogWrapper.propTypes = {
  visible: PropTypes.bool.isRequired,
  autoGrow: PropTypes.bool.isRequired,
};

const OverlayHeaderWrapper = styled.div`
  display: flex;
  flex-direction: row;
  padding: 20px 10px 0;
  height: ${props => (props.hasTitle ? '56px' : '0')};
  border-bottom: ${props =>
    props.hasTitle && `1px solid var(--borderColorLight)`};
`;

const OverlayTitle = styled.h2`
  font-size: 16px;
  line-height: 1em;
  font-weight: 500;
  padding: 0 0 20px 10px;
  flex: 1;
`;

const OverlayCloseButton = styled.button`
  position: absolute;
  right: 13px;
  top: 13px;
  border: 0;
  outline: none;
  cursor: pointer;
  border-radius: 5px;
`;
OverlayCloseButton.propTypes = {
  onClick: PropTypes.func.isRequired,
};

const OverlayCloseIcon = styled.i.attrs({ className: 'icon-closeBtn' })`
  font-size: 30px;
`;

const OverlayClose = ({ onClick }) => (
  <OverlayCloseButton onClick={onClick}>
    <OverlayCloseIcon />
  </OverlayCloseButton>
);
OverlayClose.propTypes = {
  onClick: PropTypes.func.isRequired,
};

const OverlayHeader = ({ title, onCloseClick, visibleCloseButton }) => (
  <OverlayHeaderWrapper hasTitle={!!title}>
    {title && <OverlayTitle>{title}</OverlayTitle>}
    {visibleCloseButton && (
      <OverlayClose onClick={onCloseClick}>X</OverlayClose>
    )}
  </OverlayHeaderWrapper>
);
OverlayHeader.propTypes = {
  title: PropTypes.string,
  onCloseClick: PropTypes.func.isRequired,
  visibleCloseButton: PropTypes.bool,
};

const OverlayContent = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

export default class Dialog extends Component {
  constructor(props) {
    super(props);

    // Create child div to insert in #dialog, so we can render multiple
    // modals simultaneously
    this.domNode = document.createElement('div');
  }

  componentDidMount() {
    document.getElementById('dialog').appendChild(this.domNode);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.visible !== this.props.visible) {
      this.toggle();
    }
  }

  @autobind
  toggle() {
    if (this.props.visible) {
      if (this.props.beforeClose) {
        this.props
          .beforeClose()
          .then(() => {
            this.props.onClose();
          })
          .catch(() => {});
      } else {
        this.props.onClose();
      }
    } else {
      this.props.onOpen();
    }
  }

  @autobind
  handleMaskClick(e) {
    if (e.target === e.currentTarget) {
      this.toggle();
    }
  }

  @autobind
  handleCloseClick() {
    this.toggle();
  }

  render() {
    return ReactDOM.createPortal(
      <DialogMask
        className="dialogMask"
        visible={this.props.visible}
        onClick={this.handleMaskClick}
        backgroundColor={this.props.backgroundColor}
        data-cy={this.props.name || null}
      >
        <DialogWrapper
          className="foregroundPanel"
          visible={this.props.visible}
          autoGrow={this.props.autoGrow}
        >
          {this.props.header && (
            <OverlayHeader
              title={this.props.title}
              onCloseClick={this.handleCloseClick}
              visibleCloseButton={this.props.visibleCloseButton}
            />
          )}
          <OverlayContent>{this.props.children}</OverlayContent>
        </DialogWrapper>
      </DialogMask>,
      this.domNode,
    );
  }
}
Dialog.propTypes = {
  title: PropTypes.string,
  header: PropTypes.bool,
  visible: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
  beforeClose: PropTypes.func,
  onClose: PropTypes.func,
  onOpen: PropTypes.func,
  autoGrow: PropTypes.bool,
  backgroundColor: PropTypes.string,
  visibleCloseButton: PropTypes.bool,
  name: PropTypes.string,
};
Dialog.defaultProps = {
  autoGrow: false,
  title: null,
  header: true,
  beforeClose: null,
  onClose: noop,
  onOpen: noop,
  visibleCloseButton: true,
};

/**
 * <Tabs>
 *   <Tab>
 * </Tabs>
 * <TabContent />
 * <TabContent />
 */
export const Tabs = styled.div`
  height: 40px;
  display: flex;
  margin: -20px -20px 20px;
  padding: 0 20px;
  border-bottom: 1px solid var(--borderColorLight);
`;

export const Tab = styled.div`
  height: 40px;
  padding: ${props => (props.selected ? '0 20px 6px' : '0 20px')};
  color: ${props => props.selected && 'var(--primaryColor)'};
  border-bottom: ${props =>
    props.selected ? `2px solid var(--primaryColor) !important` : 'none'};
  margin-right: 6px;
  text-decoration: none;
  border: 0;
  font-size: 14px;
  font-weight: 500;
  outline: none;
  cursor: pointer;
  position: relative;
  line-height: 20px;
`;

export const TabBadge = styled.div`
  position: absolute;
  top: -8px;
  right: 4px;
  background: ${variables.colors.orange};
  color: ${variables.colors.white};
  height: 16px;
  line-height: 16px;
  font-size: 10px;
  padding: 0 3px;
  border-radius: 8px;
  min-width: 16px;
  text-align: center;
  animation: 0.5s ${popout} ease-out;
`;

export const TabContent = styled.div`
  display: ${props => (props.visible ? 'block' : 'none')};
`;

/**
 * <Footer>
 *   <Button>
 * </Footer>
 */
export const Footer = styled.div`
  border-radius: 0 0 5px 5px;
  margin: 0 -15px -15px -15px;
  padding: 15px;
`;
Footer.displayName = 'Footer';
